import groq from "groq"

import { groqArticleProjection } from "@/graphql/schemas/shared/sanity/article.projection"
import { CommerceService } from "@/services/commerce"

import { addColorAttribute } from "../../article/queries/utils/attributes"
import { getCustomerSkuPrices } from "../../price/queries/queryCustomerSkuPrices"
import { getSkuPrice } from "./getPrices"
import { getListingByBranch } from "./getVariantListing"

const commerceApi = CommerceService.api()

export async function getArticlesBySku({ context, skus, branchId, customerNumber, customerId }) {
  try {
    const draftsFilter = `&& !(_id in path("drafts.**"))`
    const client = context.sanityClient
    let articles
    articles = await client.fetch(
      groq`*[_type=="article" && sku in $skus ${draftsFilter}] {
        ...,
        ${groqArticleProjection()}
      }`,
      { skus },
    )

    articles = await getEnrichedArticles({
      articles: articles,
      branchId,
      customerNumber,
      customerId,
    })
    return articles || []
  } catch (error) {
    console.error("getArticlesBySku()", error)
    return []
  }
}

export async function getEnrichedArticles({ articles, branchId, customerNumber, customerId, productKey = null }) {
  if (articles) {
    const listings = await getListingByBranch({
      variants: articles,
      branchKey: branchId,
    })
    const skus = articles.map((article) => {
      return article?.sku
    })

    let prices = null
    if (customerNumber || customerId) {
      const customerPrices = await getCustomerSkuPrices({
        customerId: customerId,
        customerKey: null,
        customerNumber,
        sku: skus,
        country: null,
      })
      prices = customerPrices?.items
    } else {
      prices = await Promise.all(
        skus.map(async (item) => {
          const price = await getSkuPrice({
            sku: item,
            channels: null,
            group: null,
            country: null,
          })

          return price
        }),
      )
    }

    const articleProjections = await getArticleProjections({ skus })

    for (let article of articles) {
      article.price = prices?.find((price) => price?.sku === article?.sku)
      const listingVariant =
        listings instanceof Array ? listings.find((listing) => listing?.variantSku === article?.sku) : null

      article.availability = listingVariant?.availability || "unknown"

      const articleProjection = articleProjections?.find((projection) => {
        if (projection?.masterVariant?.sku === article?.sku) return true
        if (projection?.variants?.find((variant) => variant?.sku === article?.sku)) return true
        return false
      })

      const articleConfig = getProductProjectionAttributes(articleProjection, article?.sku)

      article.amountBilling = articleConfig?.amountBilling
      article.sellableInDecimals = articleConfig?.sellableInDecimals
      addColorAttribute(article)
    }

    return articles
  }
  return []
}

/**
 * Retrieves the article information from commercetools
 *
 * @param {{ sku: string }}
 *
 * @returns {Promise<{amountBilling: string|null, sellableInDecimals: bool|null, articlePurchasePrice: object|null, articleSuggestedSellingPriceGross: object|null}>}
 */
export const getArticleConfiguration = async ({ sku }) => {
  const productProjections = (
    await commerceApi
      .productProjections()
      .get({
        queryArgs: {
          where: `key="orphan-article-${sku}" and (masterVariant(sku="${sku}") or variants(sku="${sku}"))`,
        },
      })
      .execute()
  )?.body?.results

  if (!productProjections || productProjections.length === 0) {
    return null
  }

  const productProjection = productProjections[0]
  if (!productProjection?.published) return null

  return getProductProjectionAttributes(productProjection, sku)
}

export const getArticleProjections = async ({ skus }) => {
  if (!skus || skus.length === 0) return null

  const ctReadySkus = skus.map((sku) => `"${sku}"`).join(",")

  const productProjections = (
    await commerceApi
      .productProjections()
      .get({
        queryArgs: {
          where: `masterVariant(sku in (${ctReadySkus})) or variants(sku in (${ctReadySkus}))`,
          limit: 500,
        },
      })
      .execute()
  )?.body?.results

  if (!productProjections || productProjections.length === 0) {
    return null
  }

  return productProjections
}

/**
 * Takes variants attributes and returns target variant attribute based on name
 *
 * @param {{ attributes: Array, attributeName: String }}
 * @returns { any|null }
 */
const findVariantAttribute = ({ attributes, attributeName }) => {
  if (!attributes || attributes?.length === 0) return null

  const targetAttribute = attributes.find((attribute) => attribute.name === attributeName)

  if (!targetAttribute) return null

  return targetAttribute?.value
}

/**
 * Find product atribute values from product projection
 *
 * @param {object} productProjection
 * @param {string} sku
 * @returns {{amountBilling: string|null, sellableInDecimals: bool|null, articlePurchasePrice: object|null, articleSuggestedSellingPriceGross: object|null}}
 */
export const getProductProjectionAttributes = (productProjection, sku) => {
  if (!productProjection) {
    return {
      amountBilling: null,
      sellableInDecimals: false,
      articlePurchasePrice: null,
      articleSuggestedSellingPriceGross: null,
    }
  }

  let targetVariant

  if (productProjection?.masterVariant?.sku === sku) {
    targetVariant = productProjection.masterVariant
  }

  if (!targetVariant && productProjection.variants?.length >= 1) {
    targetVariant = productProjection.variants.find((variant) => variant.sku === sku)
  }

  if (!targetVariant || targetVariant?.attributes?.length === 0) return null

  const amountBilling = findVariantAttribute({
    attributes: targetVariant?.attributes,
    attributeName: "amount-billing",
  })
  const sellableInDecimals = findVariantAttribute({
    attributes: targetVariant?.attributes,
    attributeName: "sellableInDecimals",
  })

  const articlePurchasePrice = findVariantAttribute({
    attributes: targetVariant?.attributes,
    attributeName: "purchase-price",
  })

  const articleSuggestedSellingPriceGross = findVariantAttribute({
    attributes: targetVariant?.attributes,
    attributeName: "suggested-selling-price-gross",
  })

  return {
    amountBilling,
    sellableInDecimals,
    articlePurchasePrice,
    articleSuggestedSellingPriceGross,
  }
}

/**
 *
 * @param {{
 * sanityArticles: Array,
 * algoliaArticles: Array,
 * loggedInBranch: string,
 * skus: Array<string>
 * }}
 * @returns {Promise<Array>}
 */
export async function getEnrichedArticlesFromSanityObject({ sanityArticles, algoliaArticles, loggedInBranch, skus }) {
  if (!algoliaArticles || algoliaArticles.length === 0) return []

  const articleListing = await getListingByBranch({
    variants: sanityArticles,
    branchKey: loggedInBranch,
  })

  const articleProjections = await getArticleProjections({ skus })

  let articles = []

  for (let article of algoliaArticles) {
    let sanityArticle = sanityArticles?.find((sanityArticle) => sanityArticle?.sku === article.sku) || {}
    const articleSku = article?.sku

    const listingVariant =
      articleListing instanceof Array ? articleListing?.find((listing) => listing?.variantSku === articleSku) : null

    sanityArticle.availability = listingVariant?.availability || "unknown"

    const articleProjection = articleProjections?.find((projection) => {
      if (projection?.masterVariant?.sku === articleSku) return true
      if (projection?.variants?.find((variant) => variant?.sku === articleSku)) return true

      return false
    })

    const articleConfig = getProductProjectionAttributes(articleProjection, articleSku)
    sanityArticle.id = article.objectID
    sanityArticle.amountBilling = articleConfig?.amountBilling
    sanityArticle.sellableInDecimals = articleConfig?.sellableInDecimals
    sanityArticle.type = article?._type
    sanityArticle.title = article?.title || sanityArticle?.title || ""
    sanityArticle.altTitle = article?.altTitle || sanityArticle?.altTitle || ""
    sanityArticle.image = article?.image || null
    sanityArticle.variants = article?.variants ? article.variants.map((variant) => addColorAttribute(variant)) : []
    addColorAttribute(sanityArticle)
    articles.push(sanityArticle)
  }

  return articles
}
